import React from "react";
import { CheckCircle2, CircleSlash2, ShieldCheck } from "lucide-react";
import Container from "./Container";

import { IMAGES, LANDING_COPY } from "../constants";

const ProgramSections: React.FC = () => {
  const { challenge, strategicPartner, identity, includes, whyChoose, fit } =
    LANDING_COPY;

  return (
    <>
      <section
        id="reto"
        className="py-20 md:py-28 bg-surface"
        aria-labelledby="reto-heading"
      >
        <Container>
          <div className="max-w-4xl mb-12 md:mb-16">
            <h2
              id="reto-heading"
              className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-text-primary leading-tight mb-7 text-balance"
            >
              {challenge.title}
            </h2>
            <p className="text-lg md:text-xl text-text-secondary leading-relaxed">
              {challenge.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {challenge.problems.map((problem, index) => (
              <article
                key={problem}
                className="card-hover bg-background border border-border-strong px-7 py-7 accent-line-left pl-8"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <p className="text-text-primary leading-relaxed">{problem}</p>
              </article>
            ))}
          </div>

          <div className="mt-10 md:mt-12">
            <img
              src={IMAGES.diagnostic}
              alt="Diagnostico operativo"
              loading="lazy"
              decoding="async"
              className="w-full shadow-lg object-cover object-center aspect-[4/3] md:aspect-[16/9]"
            />
          </div>
        </Container>
      </section>

      <section
        id="socio"
        className="py-20 md:py-28 bg-background"
        aria-labelledby="socio-heading"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-start">
            <div>
              <h2
                id="socio-heading"
                className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-text-primary mb-6 text-balance"
              >
                {strategicPartner.title}
              </h2>
              <p className="text-xl text-text-primary font-semibold mb-5">
                {strategicPartner.intro}
              </p>
              <div className="space-y-3">
                {strategicPartner.affirmations.map((item) => (
                  <p
                    key={item}
                    className="text-lg text-text-secondary leading-relaxed"
                  >
                    {item}
                  </p>
                ))}
              </div>
            </div>

            <div className="card-hover bg-surface border border-border-strong px-7 py-7 shadow-md">
              <h3 className="text-xl font-bold text-text-primary mb-5">
                Te damos:
              </h3>
              <ul className="space-y-4">
                {strategicPartner.deliverables.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      className="w-5 h-5 mt-0.5 text-primary flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-text-secondary leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <img
                  src={IMAGES.operations}
                  alt="Operacion en destino"
                  loading="lazy"
                  decoding="async"
                  className="w-full shadow-md object-cover object-center aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section
        id="identidad"
        className="py-20 md:py-28 bg-surface"
        aria-labelledby="identidad-heading"
      >
        <Container>
          <h2
            id="identidad-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-text-primary mb-12 text-balance"
          >
            {identity.title}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <article className="card-hover bg-primary/[0.04] border border-primary/25 px-7 py-7">
              <h3 className="text-xl font-bold text-text-primary mb-5">
                Somos:
              </h3>
              <ul className="space-y-4">
                {identity.somos.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <ShieldCheck
                      className="w-5 h-5 mt-0.5 text-primary flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="card-hover bg-background border border-border-strong px-7 py-7">
              <h3 className="text-xl font-bold text-text-primary mb-5">
                No somos:
              </h3>
              <ul className="space-y-4">
                {identity.noSomos.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CircleSlash2
                      className="w-5 h-5 mt-0.5 text-secondary flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="mt-10 md:mt-12">
            <img
              src={IMAGES.differentiation}
              alt="Diferenciacion de propuesta"
              loading="lazy"
              decoding="async"
              className="w-full shadow-lg object-cover object-center aspect-[4/3] md:aspect-[16/9]"
            />
          </div>

          <p className="mt-10 text-lg md:text-xl font-semibold text-primary">
            {identity.closing}
          </p>
        </Container>
      </section>

      <section
        id="incluye"
        className="py-20 md:py-28 bg-background"
        aria-labelledby="incluye-heading"
      >
        <Container>
          <div className="max-w-4xl mb-12">
            <h2
              id="incluye-heading"
              className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-text-primary mb-6 text-balance"
            >
              {includes.title}
            </h2>
            <p className="text-xl text-text-secondary">{includes.intro}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {includes.items.map((item, index) => (
              <article
                key={item}
                className="card-hover bg-surface border border-border-strong px-7 py-7 accent-line-left pl-8"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <p className="text-text-primary leading-relaxed">{item}</p>
              </article>
            ))}
          </div>

          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            <img
              src={IMAGES.logistics}
              alt="Hoteles y logistica"
              loading="lazy"
              decoding="async"
              className="w-full shadow-md object-cover object-center aspect-[4/3]"
            />
            <img
              src={IMAGES.security}
              alt="Protocolos y seguridad"
              loading="lazy"
              decoding="async"
              className="w-full shadow-md object-cover object-center aspect-[4/3]"
            />
          </div>

          <p className="mt-10 text-lg md:text-xl font-semibold text-text-primary">
            {includes.outro}
          </p>
        </Container>
      </section>

      <section
        id="porque"
        className="py-20 md:py-28 bg-surface"
        aria-labelledby="porque-heading"
      >
        <Container>
          <div className="card-hover max-w-5xl mx-auto bg-gradient-to-br from-primary/90 to-primary px-8 py-10 md:px-12 md:py-14 text-white shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(232,200,7,0.08),_transparent_50%)]" />
            <div className="relative">
              <h2
                id="porque-heading"
                className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold mb-8 text-balance"
              >
                {whyChoose.title}
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {whyChoose.reasons.map((reason, index) => (
                  <li
                    key={reason}
                    className="bg-white/10 border border-white/15 px-5 py-4 transition-colors hover:bg-white/15"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {reason}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-lg font-semibold text-accent">
                {whyChoose.closing}
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section
        id="ideal"
        className="py-20 md:py-28 bg-background pb-28 md:pb-28"
        aria-labelledby="ideal-heading"
      >
        <Container>
          <h2
            id="ideal-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-text-primary mb-12 text-balance"
          >
            {fit.title}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <article className="card-hover bg-primary/[0.04] border border-primary/25 px-7 py-7">
              <h3 className="text-lg font-bold text-text-primary mb-5">
                Es para agencias que:
              </h3>
              <ul className="space-y-4">
                {fit.yes.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-text-primary"
                  >
                    <CheckCircle2
                      className="w-5 h-5 mt-0.5 text-primary flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="card-hover bg-surface border border-border-strong px-7 py-7">
              <h3 className="text-lg font-bold text-text-primary mb-5">
                {fit.noTitle}
              </h3>
              <ul className="space-y-4">
                {fit.no.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-text-secondary"
                  >
                    <CircleSlash2
                      className="w-5 h-5 mt-0.5 text-secondary flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="mt-10 md:mt-12">
            <img
              src={IMAGES.ideal}
              alt="Perfil de agencia ideal"
              loading="lazy"
              decoding="async"
              className="w-full shadow-lg object-cover object-center aspect-[4/3] md:aspect-[16/9]"
            />
          </div>
        </Container>
      </section>
    </>
  );
};

export default ProgramSections;
